import React, { createContext, useContext, useEffect, useState } from "react";
import {
  DOCUMENT_UPLOAD_AND_SAVE_DATA_API,
  DOCUMENT_DOWNLOAD_DATA_API,
  DOCUMENT_UPLOAD_AND_REPLACE_DATA_API,
  DOCUMENT_DELETE_DATA_API,
  DOCUMENT_GET_ALL_LABELS_API,
} from "../config/apiConfig";
import { securedFetch } from "../utils/api";
import { useUserContext } from "./UserContext";

const DocumentUploadContext = createContext();

const createRow = (row = {}) => {
  return {
    label: row.label || "",
    files: [],
    accept: row.accept,
    isMultiFileSupport: true,
    canUploadAndUpdate: true,
    canUploadAndReplace: true,
    uploading: false,
    isDownloadable: true,
    progress: 0,
    error: null,
    labelError: null,
    isPersisted: row.isPersisted ?? false,
  };
};

export const DocumentUploadProvider = ({ children }) => {
  const { loggedInUserEmail } = useUserContext() || {};
  const [rows, setRows] = useState([]);
  const [existingLabels, setExistingLabels] = useState([]);

  useEffect(() => {
    const loadLabels = async () => {
      try {
        const response = await securedFetch(DOCUMENT_GET_ALL_LABELS_API, {
          method: "GET",
        });

        if (!response.ok) {
          throw new Error("Failed to load labels");
        }

        const labels = await response.json();

        setExistingLabels(labels.map((item) => item.labelName));

        const initialRows = labels.map((item) =>
          createRow({
            label: item.labelName,
            isPersisted: true,
            isDownloadable: true,
          }),
        );

        setRows(initialRows);
      } catch (error) {
        console.error("Label load error:", error.message);
        setRows([]);
        setExistingLabels([]);
      }
    };

    loadLabels();
  }, []);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      createRow({
        label: "",
        isPersisted: false,
      }),
    ]);
  };

  const removeRow = async (rowIndex) => {
    const row = rows[rowIndex];
    if (!row) return;

    if (row.isPersisted) {
      try {
        const response = await securedFetch(
          `${DOCUMENT_DELETE_DATA_API}?label=${encodeURIComponent(row.label)}`,
          {
            method: "DELETE",
          },
        );

        if (response.ok) {
          setRows((prev) => prev.filter((_, index) => index !== rowIndex));
          alert("Row deleted successfully!");
        } else {
          throw new Error("Delete failed");
        }
      } catch (error) {
        console.error("Delete error:", error.message);
        alert("Failed to delete row");
      }
      return;
    }

    setRows((prev) => prev.filter((_, index) => index !== rowIndex));
  };

  const updateRow = (rowIndex, changes) => {
    setRows((prev) =>
      prev.map((row, index) => {
        if (index !== rowIndex) return row;

        const updatedRow = { ...row, ...changes };

        if (Object.prototype.hasOwnProperty.call(changes, "label")) {
          const duplicate = isLabelDuplicate(changes.label, rowIndex);

          updatedRow.labelError = duplicate
            ? "This label already exists. Please choose a unique label."
            : null;
        }

        return updatedRow;
      }),
    );
  };

  const attachFiles = (rowIndex, files) => {
    updateRow(rowIndex, {
      files,
      progress: 0,
      error: null,
    });
  };

  const removeFiles = (rowIndex) => {
    updateRow(rowIndex, {
      files: [],
      progress: 0,
      error: null,
    });
  };

  const uploadFiles = async (rowIndex) => {
    const row = rows[rowIndex];
    if (
      !row?.files ||
      row.files.length === 0 ||
      row.uploading ||
      row.labelError
    )
      return;

    const formData = new FormData();
    row.files.forEach((file) => formData.append("files", file));
    formData.append("label", row.label);
    formData.append("uploadedBy", loggedInUserEmail || "System");

    updateRow(rowIndex, { uploading: true, progress: 0, error: null });

    securedFetch(DOCUMENT_UPLOAD_AND_SAVE_DATA_API, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        if (response.ok) {
          alert("File Uploaded Successfully!");
          updateRow(rowIndex, {
            uploading: false,
            progress: 100,
            files: [],
            isPersisted: true,
          });
        } else {
          throw new Error("Upload failed");
        }
      })
      .catch((error) => {
        console.error("Upload error:", error.message);
        updateRow(rowIndex, {
          uploading: false,
          error: error.message,
        });
        alert("Something went wrong with the File upload!");
      });
  };

  const uploadAndReplaceFiles = async (rowIndex) => {
    const row = rows[rowIndex];
    if (
      !row?.files ||
      row.files.length === 0 ||
      row.uploading ||
      !row.canUploadAndReplace ||
      row.labelError
    ) {
      return;
    }

    const formData = new FormData();
    row.files.forEach((file) => formData.append("files", file));
    formData.append("label", row.label);
    formData.append("uploadedBy", loggedInUserEmail || "System");

    updateRow(rowIndex, { uploading: true, progress: 0, error: null });

    securedFetch(DOCUMENT_UPLOAD_AND_REPLACE_DATA_API, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        if (response.ok) {
          alert("File Uploaded and Replaced Successfully!");
          updateRow(rowIndex, {
            uploading: false,
            progress: 100,
            files: [],
            isPersisted: true,
          });
        } else {
          throw new Error("Upload failed");
        }
      })
      .catch((error) => {
        console.error("Upload error:", error.message);
        updateRow(rowIndex, {
          uploading: false,
          error: error.message,
        });
        alert("Something went wrong with the File upload!");
      });
  };

  const downloadFile = async (label) => {
    try {
      const response = await securedFetch(
        `${DOCUMENT_DOWNLOAD_DATA_API}?label=${encodeURIComponent(label)}`,
      );

      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const contentDisposition = response.headers.get("content-disposition");
        let fileName = label;
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(
            /filename\*?=(?:UTF-8''|")?([^";]+)"?/i,
          );

          if (fileNameMatch && fileNameMatch[1]) {
            fileName = decodeURIComponent(fileNameMatch[1].replace(/"/g, ""));
          }
        }
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(link.href);
        alert(
          "File Downloaded Successfully! Please check your Downloads folder.",
        );
      } else {
        throw new Error("Download failed");
      }
    } catch (error) {
      console.error("Download error:", error.message);
      alert("Failed to download");
    }
  };

  const isLabelDuplicate = (label, currentRowIndex = null) => {
    const normalized = label.trim().toLowerCase();
    if (!normalized) return false;

    const matchesExisting = existingLabels.some(
      (existing) => existing.trim().toLowerCase() === normalized,
    );

    const matchesOtherRows = rows.some((row, index) => {
      if (index === currentRowIndex) return false;
      return row.label.trim().toLowerCase() === normalized;
    });

    return matchesExisting || matchesOtherRows;
  };

  return (
    <DocumentUploadContext.Provider
      value={{
        rows,
        addRow,
        removeRow,
        updateRow,
        attachFiles,
        removeFiles,
        uploadFiles,
        uploadAndReplaceFiles,
        downloadFile,
      }}
    >
      {children}
    </DocumentUploadContext.Provider>
  );
};

export const useDocumentUploadContext = () => useContext(DocumentUploadContext);
